import { Injectable, inject } from '@angular/core';
import { from, Observable } from 'rxjs';
import { PUBLIC_MEDIA_BUCKET, supabase } from '../../core/supabase/supabase.client';
import { ImageAssetUploadService } from '../utils/image-asset-upload.service';

export interface Collection {
  id?: number;
  title: string;
  slug: string;
  collection_type?: string;
  icon_file_id?: number | null;
  banner_file_id?: number | null;
  show_banner?: boolean;
  icon_file?: { bucket: string, path: string };
  banner_file?: { bucket: string, path: string };
  background_color?: string;
  text_color?: string;
  status?: string;
  is_active?: boolean;
  start_at?: string;
  end_at?: string;
  created_at?: string;
  updated_at?: string;
}

@Injectable({ providedIn: 'root' })
export class CollectionsService {
  private readonly imageAssets = inject(ImageAssetUploadService);
  private readonly bucket = PUBLIC_MEDIA_BUCKET;

  getCollections(): Observable<Collection[]> {
    return from(
      supabase.from('collections').select(`
        *,
        icon_file:files!collections_icon_file_id_fkey(bucket, path),
        banner_file:files!collections_banner_file_id_fkey(bucket, path)
      `).order('created_at', { ascending: false }).then((res: any) => {
        if (res.error) throw res.error;
        return res.data as Collection[];
      })
    );
  }

  getCollection(id: number): Observable<Collection> {
    return from(
      supabase.from('collections').select(`
        *,
        icon_file:files!collections_icon_file_id_fkey(bucket, path),
        banner_file:files!collections_banner_file_id_fkey(bucket, path)
      `).eq('id', id).single().then((res: any) => {
        if (res.error) throw res.error;
        return res.data as Collection;
      })
    );
  }

  createCollection(col: Partial<Collection>): Observable<Collection> {
    return from(
      supabase.from('collections').insert([col]).select().single().then((res: any) => {
        if (res.error) throw res.error;
        return res.data as Collection;
      })
    );
  }

  updateCollection(id: number, col: Partial<Collection>): Observable<Collection> {
    return from(
      supabase.from('collections').update(col).eq('id', id).select().single().then((res: any) => {
        if (res.error) throw res.error;
        return res.data as Collection;
      })
    );
  }

  deleteCollection(id: number): Observable<void> {
    return from(
      supabase.from('collections').delete().eq('id', id).then((res: any) => {
        if (res.error) throw res.error;
      })
    );
  }

  getCollectionSections(collectionId: number): Observable<any[]> {
    return from(
      supabase.from('collection_sections').select('*').eq('collection_id', collectionId).order('position', { ascending: true }).then((res: any) => {
        if (res.error) throw res.error;
        return res.data || [];
      })
    );
  }

  saveCollectionSections(collectionId: number, sections: any[]): Observable<void> {
    return from(
      (async () => {
        const materializedSections = await this.materializePendingUploads(sections, collectionId);
        const publishedAt = new Date().toISOString();
        
        const payload = materializedSections.map((s, index) => ({
          collection_id: collectionId,
          title: s.name || s.title,
          section_type: s.type || s.section_type,
          layout_type: s.layout_type || null,
          position: index,
          is_active: s.is_active,
          status: s.is_active ? 'published' : 'draft',
          published_at: s.is_active ? publishedAt : null,
          config: s.config || {}
        }));

        const { error: resDeleteError } = await supabase.from('collection_sections').delete().eq('collection_id', collectionId);
        if (resDeleteError) throw resDeleteError;
        
        if (payload.length > 0) {
          const { error: resInsertError } = await supabase.from('collection_sections').insert(payload);
          if (resInsertError) throw resInsertError;
        }
      })()
    );
  }

  private async uploadSectionImageFile(
    file: File,
    entityId: string | number = 'draft'
  ): Promise<{
    file_id: number;
    image_url: string;
    bucket: string;
    path: string;
    variants: Record<string, string>;
    version: string;
  }> {
    const safeEntityId = String(entityId || 'draft').replace(/[^a-zA-Z0-9_-]/g, '-');
    const asset = await this.imageAssets.upload({
      file,
      bucket: this.bucket,
      basePath: `collections/sections/${safeEntityId}`,
      kind: 'banner',
      entityType: 'collection_section',
      entityId: !isNaN(Number(entityId)) && entityId !== 'draft' ? Number(entityId) : null,
      purpose: 'cms_image',
    });

    return {
      file_id: asset.fileId,
      image_url: asset.masterUrl,
      bucket: asset.bucket,
      path: asset.masterPath,
      variants: asset.variants,
      version: asset.version,
    };
  }

  private async materializePendingUploads(sections: any[], collectionId: number): Promise<any[]> {
    const uploadNode = async (node: any, entityId: string | number) => {
      if (!node || typeof node !== 'object') return;

      if (node._pending_file instanceof File) {
        const field = node._pending_field || 'image_url';
        const baseField = field.replace('_url', '');

        const uploaded = await this.uploadSectionImageFile(
          node._pending_file,
          entityId
        );

        node[field] = uploaded.image_url;
        node[`${baseField}_file_id`] = uploaded.file_id;
        node[`${baseField}_bucket`] = uploaded.bucket;
        node[`${baseField}_path`] = uploaded.path;
        node[`${baseField}_variants`] = uploaded.variants;
        node[`${baseField}_version`] = uploaded.version;
      }

      delete node._pending_file;
      delete node._pending_field;
      delete node._pending_upload_key;

      for (const value of Object.values(node)) {
        if (Array.isArray(value)) {
          for (const item of value) {
            await uploadNode(item, entityId);
          }
        } else if (value && typeof value === 'object') {
          await uploadNode(value, entityId);
        }
      }
    };

    const cloned = structuredClone(sections);
    
    for (const section of cloned) {
      await uploadNode(section.config, section.id || collectionId);
    }
    
    return cloned;
  }

  uploadCollectionMedia(file: File, purpose: 'icon' | 'banner'): Observable<number> {
    return from(
      (async () => {
        const asset = await this.imageAssets.upload({
          file,
          bucket: this.bucket,
          basePath: `collections/${purpose}`,
          kind: purpose === 'icon' ? 'home_tab_icon' : 'banner',
          entityType: 'collection',
          purpose,
          isPrimary: true,
        });
        return asset.fileId;
      })()
    );
  }
}
